import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  EnvironmentKey,
  ConfigEntityDetail,
  EntityIndexedDBEntry,
  delay,
  importEntitiesDB,
  hasHttpStatus,
} from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import { useLocales } from '../use-locales/use-locales';
import { useAbortController } from '../use-abort-controller/use-abort-controller';
import { useImportEntityIdTransformer } from '../use-import-entity-id-transformer/use-import-entity-id-transformer';
import { useEntityReference } from '../use-entity-reference/use-entity-reference';
import { useResolvedEntity } from '../use-resolved-entity/use-resolved-entity';
import { useConfigEntity } from '../use-config-entity/use-config-entity';
import { useUserInfo } from '../use-user-info/use-user-info';
import { useEntityCreate } from '../use-entity-create/use-entity-create';
import { prepareEntityBodyData } from '../../utilities/prepare-entity-body-data/prepare-entity-body-data';
import { assertCompletionCriteriaPublishable } from '../../utilities/assert-completion-criteria-publishable/assert-completion-criteria-publishable';
import { createTranslationsFilterTransformer } from '../../utilities/create-translations-filter-transformer/create-translations-filter-transformer';
import { createTenantScopedTransformer } from '../../utilities/create-tenant-scoped-transformer/create-tenant-scoped-transformer';
import { buildOperationErrorDetail } from '../../utilities/build-operation-error-detail/build-operation-error-detail';
import { batchAsyncOperations } from '../../utilities/batch-async-operations/batch-async-operations';
import {
  REDUCED_RATE_LIMIT_INTERVAL_MS,
  shouldUseReducedRateLimit,
} from '../../utilities/reduced-rate-limit/reduced-rate-limit';
import {
  createBulkActionReportingStore,
  type BulkActionReportingStore,
} from '../../bulk-action-reporting/bulk-action-reporting.store';
import { withBestEffortReporting } from '../../bulk-action-reporting/with-best-effort-reporting';

const HTTP_CONFLICT = 409;

/**
 * Concurrency limit for ID transformation API calls during import.
 * Too many concurrent requests can crash the browser.
 *
 * Higher = faster but more in-flight requests / more memory pressure.
 */
const IMPORT_TRANSFORM_CONCURRENCY = 5;

/**
 * Batch size for reading entities from IndexedDB during import.
 * Keep this small to avoid materializing large payloads into memory at once.
 *
 * Higher = faster but higher peak memory (because we `bulkGet` the full payloads for a batch).
 */
const IMPORT_DB_BATCH_SIZE = 25;

/**
 * Yield back to the browser for one "turn" of the event loop.
 *
 * Even with a 0ms delay, `await yieldToBrowser()` pauses our import work until the
 * next tick, giving the browser a chance to:
 * - paint UI updates (so the tab doesn't look frozen)
 * - handle user input (scroll/click) and other queued callbacks
 * - run garbage collection (helps prevent huge peak-memory/GC pressure)
 *
 * This doesn't "slow the import down" in a meaningful way; it prevents a single
 * long-running JS task from monopolizing the main thread during very large imports.
 */
const yieldToBrowser = () => delay(0);

type TransformEntitiesResult = {
  originalEntity: EntityIndexedDBEntry;
  transformedEntity: ConfigEntityDetail;
} & (
  | { transformStatus: 'pending' }
  | { transformStatus: 'skip' }
  | {
      transformStatus: 'error';
      /** The error thrown by the transform — recorded so the entity is
       *  retryable rather than silently skipped. */
      transformError: unknown;
    }
);

export type UseImportEntitiesOptions = {
  domainEntityType: string;
  currentAuthoringEnvironment: EnvironmentKey;
  tenantId: string;
  bulkActionReportingStore?: BulkActionReportingStore;
};

/** Process items sequentially, returning a promise that resolves when all are done */
const processSequentially = <T>(
  items: T[],
  handler: (item: T) => Promise<void>,
): Promise<void> =>
  items.reduce<Promise<void>>(
    (chain, item) => chain.then(() => handler(item)),
    Promise.resolve(),
  );

/**
 * Process items in fixed-size batches, sequentially.
 *
 * What this does:
 * - takes `items` and splits them into slices of `batchSize`
 * - calls `await handler(batch)` for batch 0, then batch 1, etc.
 *
 * Why not a loop?
 * - We *intentionally* want sequential batches (bounded memory / less GC pressure).
 *   Using `Promise.all` over batches would run many batches at once and can bring
 *   back the “load everything into memory” crash behavior on very large imports.
 *
 * This implementation avoids recursion as well (no stack growth on huge inputs).
 */
const processInBatches = <T>(
  items: T[],
  batchSize: number,
  handler: (batch: T[], startIndex: number) => Promise<void>,
): Promise<void> => {
  const batchCount = Math.ceil(items.length / batchSize);
  const batchStartIndexes = Array.from(
    { length: batchCount },
    (_, i) => i * batchSize,
  );

  return batchStartIndexes.reduce<Promise<void>>(
    (chain, startIndex) =>
      chain.then(() => {
        const batch = items.slice(startIndex, startIndex + batchSize);
        return handler(batch, startIndex);
      }),
    Promise.resolve(),
  );
};

/**
 * Hook that returns a callback to import entities staged in IndexedDB
 * for a given domain entity type.
 *
 * This import path is designed to handle very large imports (thousands of entities
 * with large payloads) without crashing the browser tab.
 *
 * High-level flow:
 * 1) Query IndexedDB for the *pending entity IDs* (status: 'ready' | 'error') for
 *    this `domainEntityType` (cheap: uses indexes; does NOT load entity payloads).
 * 2) Process those IDs in small batches:
 *    - `bulkGet()` the next batch of entries (bounded memory)
 *    - Transform IDs with limited concurrency (each transform may do 1+ API calls)
 *    - Persist the transformed payloads back to IndexedDB in a single `bulkPut()`
 *    - Create entities sequentially (one-by-one) to avoid huge request/memory spikes
 * 3) Build the import-results CSV by reading IndexedDB in batches (again: no `toArray()`
 *    for the full dataset).
 *
 * Notes:
 * - `domainEntityType` is treated as immutable for a staged entry; we preserve it from
 *   the original staged entity when persisting transformed payloads.
 *
 * @param options.domainEntityType The domain entity type to import.
 */
export const useImportEntities = ({
  domainEntityType,
  currentAuthoringEnvironment,
  tenantId,
  bulkActionReportingStore,
}: UseImportEntitiesOptions) => {
  const reportingStore = useMemo(
    () => bulkActionReportingStore ?? createBulkActionReportingStore(),
    [bulkActionReportingStore],
  );
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const { locales: tenantLocales } = useLocales();
  const entityConfig = useEntityReference({ entityType: domainEntityType });
  const { entity: domainEntity } = useResolvedEntity({
    entityId: entityConfig.id,
  });

  if (!domainEntity) {
    throw new Error(
      `useImportEntities: Domain entity not found for type "${domainEntityType}"`,
    );
  }

  const configEntity = useConfigEntity({ domainEntity });

  // Import requires the entity to have a `create` endpoint. Some entities (e.g. `test-user`,
  // which runs against its own non-CACT-BE backend) don't support create at all; for those
  // the BulkActionsProvider still mounts this hook, so we must not crash. The returned
  // callbacks check `isImportSupported` and bail out cleanly instead of throwing during render.
  const createEndpoint = configEntity.endpoints.create;
  const isImportSupported = Boolean(createEndpoint);

  const { userInfo } = useUserInfo();

  const { mutateAsync: createEntity } = useEntityCreate({
    entity: configEntity,
    environment: currentAuthoringEnvironment,
    operation: 'create',
  });

  const importEntityIdTransformer = useImportEntityIdTransformer({
    targetEnvironment: currentAuthoringEnvironment,
    tenantId,
  });

  const { createAbortController, abort: cancelImport } = useAbortController();

  const importAllEntities = useCallback(
    async ({ isRetry = false } = {}) => {
      if (!createEndpoint) {
        // Entity doesn't support `create` (e.g. `test-user`). The provider still mounted this
        // hook; just no-op so the UI doesn't crash if a stray import action fires.
        return;
      }
      const abortController = createAbortController();

      const throwIfAborted = () => {
        if (abortController.signal.aborted) {
          throw new DOMException('Operation was cancelled', 'AbortError');
        }
      };
      const shouldUseReducedImportCreateRateLimit =
        shouldUseReducedRateLimit(domainEntityType);
      /**
       * NOTE: we intentionally call `importEntitiesDB` directly here instead of using
       * `useImportEntitiesIndexedDB` (which uses `useLiveQuery` and can materialize large arrays).
       * Import is an "action" flow; we want bounded batch reads to prevent browser crashes.
       */
      const pendingImportCount =
        await importEntitiesDB.getPendingImportCountByDomainEntityType(
          domainEntityType,
        );

      if (pendingImportCount === 0) {
        // No entities to import - should not happen as UI disables the button
        return;
      }

      // On retry, the session is already resumed via resumeSessionForRetry() —
      // skip session creation so we don't discard the existing results.
      if (!isRetry) {
        await withBestEffortReporting(async () => {
          await reportingStore.getState().createSession({
            operationType: 'import',
            domainEntityType,
            totalItems: pendingImportCount,
            targetEnvironment: currentAuthoringEnvironment,
            metadata: {
              tenantId,
            },
          });
        });
      }

      const tenantLocaleCodes = (tenantLocales ?? []).map(
        (locale) => locale.code,
      );

      const pendingImportIds =
        await importEntitiesDB.getPendingImportEntityIdsByDomainEntityType(
          domainEntityType,
        );

      pendingImportIds.forEach((entityId) => {
        withBestEffortReporting(() => {
          reportingStore.getState().recordItemResult({
            sourceEntityId: entityId,
            entityType: domainEntityType,
            status: 'loading',
          });
        });
      });

      /**
       * Transform entity IDs with batched concurrency to prevent browser crashes.
       * Each entity transformation makes 1+ API calls to check if entities exist,
       * so we limit concurrency to avoid overwhelming the browser with requests.
       */
      const translationsFilterTransformer =
        createTranslationsFilterTransformer(tenantLocaleCodes);

      let errorCount = 0;

      /**
       * Invalidates every query scoped to `currentAuthoringEnvironment` so the
       * entity list reflects newly-imported entities without waiting for
       * react-query staleTime. Called eagerly on every terminal path (full
       * success, partial success, error, partial-failure catch) so the list
       * refreshes while the reporting modal is still open. Note: very large
       * imports (1000+) can outpace backend indexing — if a missing entity is
       * reported, retrying the read or triggering another invalidation
       * upstream is the user's recourse since we no longer schedule a
       * deferred re-invalidation here.
       */
      const invalidateEntityListQueries = () => {
        queryClient.invalidateQueries({
          queryKey: [currentAuthoringEnvironment],
        });
      };

      try {
        // Create transformer factory once - schema paths are computed upfront
        const tenantScopedTransformerFactory = createTenantScopedTransformer(
          configEntity.schema,
        );

        await processInBatches(
          pendingImportIds,
          IMPORT_DB_BATCH_SIZE,
          async (batchIds) => {
            throwIfAborted();

            const batchEntries = await importEntitiesDB.entities.bulkGet(
              batchIds,
            );
            const definedEntries = batchEntries.filter(
              (batchEntry): batchEntry is EntityIndexedDBEntry =>
                Boolean(batchEntry),
            );

            const { results: transformedIdEntities } =
              await batchAsyncOperations({
                items: definedEntries,
                operation: async (
                  entity: EntityIndexedDBEntry,
                ): Promise<TransformEntitiesResult> => {
                  try {
                    // Retry safety: if this entry was already transformed in a prior attempt
                    // (i.e., we persisted transformed payloads to IndexedDB), reuse it as-is
                    // so we don't "double transform" IDs on retries.
                    const isAlreadyTransformed =
                      Boolean(entity.newId) && entity.data.id !== entity.id;
                    if (isAlreadyTransformed) {
                      return {
                        originalEntity: entity,
                        transformStatus: 'pending',
                        transformedEntity: entity.data,
                      };
                    }

                    const { transformed, data } =
                      await importEntityIdTransformer(entity.data);
                    return {
                      originalEntity: entity,
                      transformStatus: transformed ? 'pending' : 'skip',
                      transformedEntity: data,
                    };
                  } catch (transformError) {
                    // eslint-disable-next-line no-console
                    console.error('[import-transform] Transform failed', {
                      entityId: entity.id,
                      error: transformError,
                    });
                    return {
                      originalEntity: entity,
                      transformStatus: 'error',
                      transformError,
                      // Holds the *original* (untransformed) entity data because
                      // the transform never completed. On retry the
                      // `isAlreadyTransformed` check (`entity.newId` is truthy and
                      // `entity.data.id !== entity.id`) will still be false,
                      // so the entity will be re-transformed correctly.
                      transformedEntity: entity.data,
                    };
                  }
                },
                concurrency: IMPORT_TRANSFORM_CONCURRENCY,
                continueOnError: true,
                abortSignal: abortController.signal,
              });

            const skippedInBatch = transformedIdEntities.filter(
              (r) => r.transformStatus === 'skip',
            );
            const transformErrorsInBatch = transformedIdEntities.filter(
              (
                r,
              ): r is TransformEntitiesResult & {
                transformStatus: 'error';
                transformError: unknown;
              } => r.transformStatus === 'error',
            );
            const pendingImportsInBatch = transformedIdEntities.filter(
              (r) => r.transformStatus === 'pending',
            );

            await skippedInBatch.reduce(async (previous, entry) => {
              await previous;
              await withBestEffortReporting(async () => {
                await reportingStore.getState().recordItemResult({
                  sourceEntityId: entry.originalEntity.id,
                  entityId: entry.transformedEntity.id,
                  domainEntityId:
                    entry.transformedEntity.attributes.entityMetadata
                      .domainEntityId,
                  entityType: entry.originalEntity.domainEntityType,
                  entityName:
                    entry.transformedEntity.attributes.entityMetadata.name,
                  status: 'skipped',
                });
              });
            }, Promise.resolve());

            // Record transform failures as errors (retryable) rather than skips
            errorCount += transformErrorsInBatch.length;
            await transformErrorsInBatch.reduce(async (previous, entry) => {
              await previous;
              const entityName =
                entry.transformedEntity.attributes?.entityMetadata?.name ??
                entry.originalEntity.id;
              const entityError = buildOperationErrorDetail(
                entry.transformError,
                formatMessage,
              );
              await withBestEffortReporting(async () => {
                await reportingStore.getState().recordItemResult({
                  sourceEntityId: entry.originalEntity.id,
                  entityType: entry.originalEntity.domainEntityType,
                  entityName,
                  status: 'error',
                  error: entityError,
                });
              });
            }, Promise.resolve());

            /**
             * Persist transformed entity payloads to IndexedDB so reporting (CSV) uses
             * the final imported IDs and normalized data shape.
             *
             * Also mark transform-skipped entries as skipped in one bounded-memory batch.
             *
             * We persist before the create step so:
             * - the CSV reflects transformed IDs even if the import fails midway
             * - IndexedDB stays in sync with what we'll attempt to create next
             */
            await importEntitiesDB.transaction(
              'rw',
              importEntitiesDB.entities,
              async () => {
                await importEntitiesDB.entities.bulkPut(
                  transformedIdEntities.map((entry) => ({
                    /**
                     * `domainEntityType` is treated as immutable for an entry,
                     * so we preserve it from the original staged entity.
                     *
                     * This keeps IndexedDB indexes and filtering consistent (including
                     * the [domainEntityType+status] compound index).
                     */
                    ...entry.originalEntity,
                    data: entry.transformedEntity,
                    newId: entry.transformedEntity.id,
                    domainEntityType: entry.originalEntity.domainEntityType,
                    status: (() => {
                      if (entry.transformStatus === 'error') return 'error';
                      if (entry.transformStatus === 'skip') return 'skipped';
                      return entry.originalEntity.status;
                    })(),
                  })),
                );
              },
            );

            // Import (create) entities sequentially to avoid overwhelming the browser/backend.
            await processSequentially(pendingImportsInBatch, async (entry) => {
              throwIfAborted();

              const entityMetadataName =
                entry.transformedEntity.attributes.entityMetadata.name;
              const entityName = entityMetadataName ?? entry.originalEntity.id;

              try {
                if (shouldUseReducedImportCreateRateLimit) {
                  await delay(REDUCED_RATE_LIMIT_INTERVAL_MS);
                }
                // Create transformer for this entity's source tenant
                const tenantScopedTransformer = tenantScopedTransformerFactory(
                  entry.originalEntity.sourceTenantId,
                  tenantId,
                );

                const transformers = [
                  translationsFilterTransformer,
                  tenantScopedTransformer,
                ];

                // An import creates the entity outright, same as a copy — last point to
                // stop it. Published only, matching the backend; a draft is not served.
                if (
                  entry.transformedEntity.attributes?.entityMetadata?.status ===
                  'published'
                ) {
                  assertCompletionCriteriaPublishable({
                    data: entry.transformedEntity,
                    domainEntityType,
                  });
                }

                const bodyData = prepareEntityBodyData({
                  data: entry.transformedEntity,
                  entity: configEntity,
                  operationEndpoint: createEndpoint,
                  userInfo,
                  transformers,
                });

                await createEntity({
                  body: JSON.stringify({ data: bodyData }),
                  domainEntityType,
                });
                await importEntitiesDB.updateEntityStatusById(
                  entry.originalEntity.id,
                  'complete',
                );
                await withBestEffortReporting(async () => {
                  await reportingStore.getState().recordItemResult({
                    sourceEntityId: entry.originalEntity.id,
                    entityId: entry.transformedEntity.id,
                    domainEntityId:
                      entry.transformedEntity.attributes.entityMetadata
                        .domainEntityId,
                    entityType: entry.originalEntity.domainEntityType,
                    entityName: entityMetadataName,
                    status: 'success',
                  });
                });
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Import failed', { entityName, error });
                /**
                 * If the API returns a 409 Conflict, treat it as a skip (entity already exists)
                 * rather than halting the entire import process.
                 */
                if (hasHttpStatus(error, HTTP_CONFLICT)) {
                  await importEntitiesDB.updateEntityStatusById(
                    entry.originalEntity.id,
                    'skipped',
                  );
                  await withBestEffortReporting(async () => {
                    await reportingStore.getState().recordItemResult({
                      sourceEntityId: entry.originalEntity.id,
                      entityId: entry.transformedEntity.id,
                      domainEntityId:
                        entry.transformedEntity.attributes.entityMetadata
                          .domainEntityId,
                      entityType: entry.originalEntity.domainEntityType,
                      entityName: entityMetadataName,
                      status: 'skipped',
                    });
                  });
                } else {
                  const entityError = buildOperationErrorDetail(
                    error,
                    formatMessage,
                  );
                  const persistedErrorMessage = [
                    entityError?.detail,
                    entityError?.secondaryDetail,
                  ]
                    .filter((part): part is string =>
                      Boolean(part && part.trim()),
                    )
                    .join(' — ')
                    .trim();
                  await importEntitiesDB.updateEntityStatusById(
                    entry.originalEntity.id,
                    'error',
                    persistedErrorMessage || undefined,
                  );
                  await withBestEffortReporting(async () => {
                    await reportingStore.getState().recordItemResult({
                      sourceEntityId: entry.originalEntity.id,
                      entityId: entry.transformedEntity.id,
                      domainEntityId:
                        entry.transformedEntity.attributes.entityMetadata
                          .domainEntityId,
                      entityType: entry.originalEntity.domainEntityType,
                      entityName: entityMetadataName,
                      status: 'error',
                      error: entityError,
                    });
                  });
                  errorCount += 1;

                  // Continue the import even when a create fails so the user
                  // sees "complete with errors" and can retry only the failures.
                }
              } finally {
                await yieldToBrowser();
              }
            });

            // Yield between batches so we don't monopolize the main thread
            await yieldToBrowser();
          },
        );

        // Immediate invalidation - list updates while modal is still open
        invalidateEntityListQueries();

        if (errorCount > 0) {
          // Use completeSession (not failSession) because the operation finished
          // processing all entities — some just failed. This gives the banner
          // the 'completedWithErrors' derived status.
          withBestEffortReporting(() => {
            reportingStore.getState().completeSession();
          });
          return;
        }

        await importEntitiesDB.transaction(
          'rw',
          importEntitiesDB.entities,
          async () => importEntitiesDB.clearAll(domainEntityType),
        );
        await withBestEffortReporting(async () => {
          await reportingStore.getState().completeSession();
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        await withBestEffortReporting(async () => {
          await reportingStore
            .getState()
            .failSession(
              error instanceof Error
                ? error.message
                : 'An error occurred while importing entities',
            );
        });
        // Immediate invalidation - successfully imported entities show up
        invalidateEntityListQueries();
      }
    },
    [
      createAbortController,
      tenantLocales,
      importEntityIdTransformer,
      queryClient,
      currentAuthoringEnvironment,
      configEntity,
      createEndpoint,
      userInfo,
      createEntity,
      domainEntityType,
      tenantId,
      reportingStore,
      formatMessage,
    ],
  );

  /**
   * Retries only the entities that failed during the initial import.
   *
   * How filtering works: `resumeSessionForRetry()` resets failed items in the
   * reporting store and returns their IDs. Meanwhile, the initial import
   * persisted each entity's outcome to IndexedDB (status = 'complete' | 'skipped'
   * | 'error'). `importAllEntities` reads from IndexedDB via
   * `getPendingImportCountByDomainEntityType` which only returns entries whose
   * status is still eligible for import ('ready' | 'error'), so completed and
   * skipped entities are naturally excluded without an explicit entity-ID filter.
   */
  const retryFailedImports = useCallback(async () => {
    const { session } = reportingStore.getState();

    // No prior session — nothing to resume, run a fresh import.
    if (!session) {
      await importAllEntities();
      return;
    }

    // Check for error items *before* calling resumeSessionForRetry so we
    // don't mutate the session and then immediately discard it with a fresh
    // import.
    const hasErrorItems = session.itemResults.some(
      (item) => item.status === 'error',
    );
    if (!hasErrorItems) {
      await importAllEntities();
      return;
    }

    reportingStore.getState().resumeSessionForRetry();
    await importAllEntities({ isRetry: true });
  }, [reportingStore, importAllEntities]);

  return {
    importAllEntities,
    retryFailedImports,
    cancelImport,
    isImportSupported,
  };
};
